import { BedDouble, ClipboardList, HeartPulse, Pill, Stethoscope, Users, UserX, type LucideIcon } from 'lucide-react';
import { ProgressBar } from '@/components/ui/ProgressBar';
import type { KpiTone } from '@/components/ui/KpiTile';
import { cn } from '@/lib/cn';
import { DashCard, fmtMin } from './DashCard';
import type { WaitTimes, WaitWindow } from './types';

const WINDOW_LABELS: Record<WaitWindow, string> = { today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days', '6m': 'Last 6 months' };

// Registration alone has a real wait/work split. Every other role reports one
// combined total: their started_at fires the moment the stage activates (see
// reports.js's ROLES_WITH_WAIT_SPLIT), so a split there would be a "0m wait"
// manufactured by the instrumentation. Those rows print "— wait" instead of a
// fake zero until per-stage "opened" timestamps exist (wireframe region 7).
// "Unassigned" is the span with no doctor picked — the row staff should treat
// as an actionable gap.
const ROLES: Array<{ key: string; label: string; sub?: string; icon: LucideIcon; tone: KpiTone; solid: string; split?: boolean }> = [
  { key: 'registration', label: 'Registration', icon: Users, tone: 'violet', solid: 'bg-tone-violet', split: true },
  { key: 'nurse', label: 'Nurse', icon: HeartPulse, tone: 'green', solid: 'bg-tone-green' },
  { key: 'assessment', label: 'Assessment', icon: ClipboardList, tone: 'blue', solid: 'bg-tone-blue' },
  { key: 'unassigned', label: 'Unassigned', sub: '(no doctor picked)', icon: UserX, tone: 'slate', solid: 'bg-tone-slate' },
  { key: 'consultant', label: 'Consultant', icon: Stethoscope, tone: 'orange', solid: 'bg-tone-orange' },
  { key: 'pharmacy', label: 'Pharmacy / Exit', icon: Pill, tone: 'teal', solid: 'bg-tone-teal' },
];

/**
 * Patient Wait Time Breakdown — docs/design/dashboard region 7: one row per
 * role with its own bar on a grey track, lighter = waiting, solid = working.
 * Bars are scaled to the largest role total, so the longest stage fills its
 * track and the others read relative to it.
 */
export function WaitBreakdownCard({ waitTimes }: { waitTimes: WaitTimes | null }) {
  const bucket = waitTimes?.selected;
  const rows = ROLES.map(r => {
    const wait = r.split ? bucket?.[`avg_${r.key}_wait_min`] ?? 0 : 0;
    const work = r.split ? bucket?.[`avg_${r.key}_work_min`] ?? 0 : bucket?.[`avg_${r.key}_total_min`] ?? 0;
    return { ...r, wait, work };
  });
  const max = Math.max(0, ...rows.map(r => r.wait + r.work));
  const los = waitTimes?.ip_length_of_stay;

  return (
    <DashCard title="Patient Wait Time Breakdown">
      <div className="mt-1.5 text-[10.5px] leading-[1.85] text-ink-muted">
        {WINDOW_LABELS[waitTimes?.selected_window ?? '30d']}{bucket?.sample_size ? ` • ${bucket.sample_size} visits` : ''}
        <br />Lighter = waiting for staff • Solid = staff actively working
      </div>
      {!bucket || max === 0 ? (
        <div className="flex flex-1 items-center justify-center py-6 text-center text-[12.5px] text-ink-muted">
          Not enough completed visits yet to compute a breakdown.
        </div>
      ) : (
        <div className="mt-1">
          {rows.map(r => {
            const Icon = r.icon;
            return (
              <div key={r.key} className="mt-[9px] grid grid-cols-[18px_96px_minmax(0,1fr)_auto] items-center gap-2 text-[10.5px]">
                <span className={cn('flex h-[18px] w-[18px] items-center justify-center rounded text-white', r.solid)}><Icon className="h-3 w-3" /></span>
                <span className="leading-tight text-ink">
                  {r.label}
                  {r.sub && <span className="block text-[8.5px] text-ink-muted">{r.sub}</span>}
                </span>
                <ProgressBar tone={r.tone} leadPct={(r.wait / max) * 100} pct={(r.work / max) * 100} label={`${r.label} time`} />
                <span className="whitespace-nowrap text-right text-[10px] tabular-nums text-ink">
                  {r.split ? `${fmtMin(r.wait)} wait / ${fmtMin(r.work)} work` : `— wait / ${fmtMin(r.work)} in stage`}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {!!los && los.sample_size > 0 && (
        <div className="mt-auto pt-3">
          <div className="flex h-[30px] items-center gap-2 rounded-md border border-line bg-muted/60 px-2 text-[11px] text-ink">
            <BedDouble className="h-4 w-4 text-teal" />
            Avg. IP length of stay: <b className="font-semibold">{los.avg_hours}h</b> across {los.sample_size} admission{los.sample_size === 1 ? '' : 's'}
          </div>
        </div>
      )}
    </DashCard>
  );
}
